import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import {
  trackPageViewV2,
  trackUserInteraction,
} from "@/services/analytics-adapter";
import {
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
} from "@/services/core-api-adapter";
import {
  isFeatureEnabled,
  FeatureToggleIdentifier,
} from "@/services/feature-toggle-adapter";
import { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import { convertPxToRem, replaceCentsFromAmount } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ConsultationTypeCardProps {
  label: string;
  subtitle: string;
  amountFormatted?: string;
  originalAmountFormatted?: string;
  isDiscountApplied?: boolean;
  onClick: () => void;
  badgeText?: string;
  ariaLabel: string;
}

function ConsultationTypeCard({
  label,
  subtitle,
  amountFormatted,
  originalAmountFormatted,
  isDiscountApplied,
  onClick,
  badgeText,
  ariaLabel,
}: ConsultationTypeCardProps) {
  const { t } = useTranslation();
  return (
    <Stack
      component="button"
      sx={{
        ...panelStyle,
        cursor: "pointer",
        border: "none",
      }}
      aria-label={ariaLabel}
      color="neutral"
      onClick={onClick}
    >
      <Stack sx={{ width: "100%" }} spacing={2}>
        <Stack spacing={1}>
          <Stack
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={1}
            alignItems="left"
          >
            <Typography variant="h3" fontWeight={600} textAlign="left">
              {label}
            </Typography>
            {badgeText && (
              <Stack
                sx={{
                  backgroundColor: "accent1.main",
                  borderRadius: 2,
                  alignSelf: "start",
                }}
                padding={1}
              >
                <Typography
                  fontWeight={600}
                  variant="body2"
                  color="primary.contrastText"
                >
                  {badgeText}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body1" color="neutral.light" textAlign="left">
              {subtitle}
            </Typography>
            <IconLoader
              sx={{ fontSize: 30 }}
              color="primary"
              icon="ChevronNextIcon"
            />
          </Stack>
        </Stack>
        {amountFormatted &&
          (isDiscountApplied ? (
            <Box
              textAlign="left"
              sx={{
                background: (theme) => theme.palette.accent4.main,
                width: convertPxToRem(130),
                borderRadius: 2,
                paddingRight: convertPxToRem(30),
                paddingLeft: convertPxToRem(20),
                paddingTop: convertPxToRem(5),
                paddingBottom: convertPxToRem(5),
              }}
            >
              <Typography color="primary.contrastText" fontWeight={600}>
                {t("ConsultationTypeSelection.discount.amount", {
                  amountFormatted: amountFormatted || "",
                })}
              </Typography>
              <Typography
                color="primary.contrastText"
                fontWeight={600}
                variant="body2"
                sx={{ textDecoration: "line-through" }}
              >
                {t("ConsultationTypeSelection.discount.original.amount", {
                  amountFormatted: originalAmountFormatted || "",
                })}
              </Typography>
            </Box>
          ) : (
            <Box
              textAlign="center"
              sx={{
                background: (theme) => theme.palette.primary.main,
                width: 90,
                borderRadius: 2,
                padding: 1,
              }}
            >
              <Typography color="primary.contrastText" fontWeight={600}>
                {amountFormatted}
              </Typography>
            </Box>
          ))}
      </Stack>
    </Stack>
  );
}

interface ConsultationTypeSelectionProps {
  onDirectToDoctorConsultClick: () => void;
  onNurseConsultClick: () => void;
  onSeeAllConditionsClick: () => void;
  onDocMedBundleClick: () => void;
  memberProfile: MemberProfile;
  onBackButtonClick?: () => void;
}

export function ConsultationTypeSelection({
  onDirectToDoctorConsultClick,
  onNurseConsultClick,
  onSeeAllConditionsClick,
  onDocMedBundleClick,
  memberProfile,
  onBackButtonClick,
}: ConsultationTypeSelectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isDocMedBundleEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_DOC_MED_BUNDLES
  );

  const directToDoctorPaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  const docMedBundlePaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
    );

  const nurseAssistedPaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
    );

  function handleBackButtonClick() {
    if (onBackButtonClick) {
      onBackButtonClick();
    } else {
      navigate(-1);
    }
  }

  function onDirectToDoctorConsultationTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | Doctor on demand",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onDirectToDoctorConsultClick();
  }

  function onNurseConsultationTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | Nurse consult",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onNurseConsultClick();
  }

  function onDocMedBundleConsultationTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | DocMeds Bundle",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onDocMedBundleClick();
  }

  function onSeeAllConditionsTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | See all conditions",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onSeeAllConditionsClick();
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: "Medical assistance:Consultation type selection",
      pageSubSection1: "Medical assistance",
      pageSubSection2: "Medical assistance:Consultation type selection",
      pageCategory: "Medical assistance",
    });
  }, []);

  return (
    <Stack
      color="neutral.dark"
      sx={{
        ...defaultFullscreenPageStyling,
        minHeight: "100%",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <Stack p={2} spacing={2}>
        <Stack>
          <CircledBackButton showLabel={true} onClick={handleBackButtonClick} />
        </Stack>
        <Stack>
          <Typography variant="h1" color="inherit">
            {t("ConsultationTypeSelection.title")}
          </Typography>

          <Stack spacing={4} py={4}>
            <ConsultationTypeCard
              label={t("ConsultationTypeSelectionV2.DirectToDoctor.title")}
              subtitle={t(
                "ConsultationTypeSelectionV2.DirectToDoctor.subtitle"
              )}
              amountFormatted={replaceCentsFromAmount(
                directToDoctorPaymentRates?.amountFormatted || ""
              )}
              originalAmountFormatted={replaceCentsFromAmount(
                directToDoctorPaymentRates?.originalAmountFormatted || ""
              )}
              isDiscountApplied={directToDoctorPaymentRates?.isDiscountApplied}
              onClick={onDirectToDoctorConsultationTypeClick}
              badgeText={t("common.mostPopular")}
              ariaLabel={t("ConsultationTypeSelectionV2.DirectToDoctor.title")}
            />

            {isDocMedBundleEnabled && (
              <ConsultationTypeCard
                label={t("ConsultationTypeSelectionV2.docMedBundle.title")}
                subtitle={t(
                  "ConsultationTypeSelectionV2.docMedBundle.subtitle"
                )}
                amountFormatted={replaceCentsFromAmount(
                  docMedBundlePaymentRates?.amountFormatted || ""
                )}
                originalAmountFormatted={replaceCentsFromAmount(
                  docMedBundlePaymentRates?.originalAmountFormatted || ""
                )}
                isDiscountApplied={docMedBundlePaymentRates?.isDiscountApplied}
                onClick={onDocMedBundleConsultationTypeClick}
                badgeText={t("common.new")}
                ariaLabel={t("ConsultationTypeSelectionV2.docMedBundle.title")}
              />
            )}

            <ConsultationTypeCard
              label={t("ConsultationTypeSelectionV2.nurseConsult.title")}
              subtitle={t(
                "ConsultationTypeSelectionV2.nurseConsult.subtitle.v2"
              )}
              amountFormatted={replaceCentsFromAmount(
                nurseAssistedPaymentRates?.amountFormatted || ""
              )}
              originalAmountFormatted={replaceCentsFromAmount(
                nurseAssistedPaymentRates?.originalAmountFormatted || ""
              )}
              isDiscountApplied={nurseAssistedPaymentRates?.isDiscountApplied}
              onClick={onNurseConsultationTypeClick}
              ariaLabel={t("ConsultationTypeSelectionV2.nurseConsult.title")}
            />

            <ConsultationTypeCard
              label={t("ConsultationTypeSelectionV2.seeConditions.title")}
              subtitle={t("ConsultationTypeSelectionV2.seeConditions.subtitle")}
              onClick={onSeeAllConditionsTypeClick}
              ariaLabel={t("ConsultationTypeSelectionV2.seeConditions.title")}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
